/**
 * The rules of the game. If you're new to this codebase, start reading here.
 *
 * How the game works, in code terms:
 * - The grid is a list of colored cells.
 * - Blocks wait in columns. Launching a block turns it into a "runner" that
 *   travels around the track (see track.ts).
 * - Each time a runner passes a row or column, it looks inward at the nearest
 *   cell. If that cell is the runner's color, the cell is removed and the
 *   runner's `count` goes down by one.
 * - A runner whose count hits zero leaves the track. A runner that gets all the
 *   way around with count left over goes into the tray, ready to be relaunched.
 *   If the tray has no room for it, the game is lost. Emptying the grid wins.
 *
 * Design decisions worth noticing:
 * - This file has no DOM, canvas, or timer code. That makes it "pure logic":
 *   the tests can run it directly, the bot (bot.ts) can play thousands of
 *   games quickly, and the level generator (level.ts) can replay a solution
 *   under exactly the same rules the player experiences.
 * - Functions like `launch` and `update` change ("mutate") the state object
 *   they are given instead of returning a new copy. That's simple and fast,
 *   but it means callers must not assume a state stays the same after passing
 *   it to one of these functions.
 * - Instead of calling the renderer or sound code directly, the rules append
 *   a record of what happened to `state.events`. Other parts of the program
 *   read that list and react (animations, sounds). This keeps the rules
 *   independent of how the game looks or sounds.
 */
import { SIDES, type Side, slotAt, trackLength } from "./track";

/**
 * A color, stored as a number: its position in the palette (see palette.ts).
 * Storing numbers instead of color strings keeps comparisons cheap and lets
 * the look of each color change without touching the rules.
 */
export type Color = number;
/**
 * One square of the grid: a color, or `null` when there is nothing there
 * (cleared, or empty from the start, as in image levels).
 */
export type Cell = Color | null;

/** The board of colored cells the runners clear. */
export interface Grid {
	width: number;
	height: number;
	/**
	 * All cells in one flat array, row after row ("row-major" order). The cell
	 * at column x, row y is at index `y * width + x`. A flat array is simpler and
	 * faster to copy than an array of arrays.
	 */
	cells: Cell[];
}

/**
 * A block: a color plus how many cells it can still clear. The same shape is
 * used while it waits in a column, sits in the tray, or (extended as a
 * `Runner`) travels the track.
 */
export interface Block {
	color: Color;
	/** How many more cells this block can clear. At zero, it's used up. */
	count: number;
	/**
	 * Optional. Blocks with the same `link` number are "linked": clicking one
	 * launches all of them together, and none leave the track until all of them
	 * are used up. The `?` means most blocks don't have this property at all.
	 */
	link?: number;
}

/**
 * A block that is currently on the track. `extends Block` means it has all of
 * Block's properties (color, count, link) plus the ones below.
 */
export interface Runner extends Block {
	/**
	 * Where the runner is along the track, measured in slots (see track.ts).
	 * It's a decimal number because runners move smoothly: 3.5 means halfway
	 * between slot 3 and slot 4. It resets to 0 after each lap.
	 */
	position: number;
	/**
	 * Position within a linked group (a "train"): 0 is the leader, 1 is next
	 * (`trainGap` slots behind it), and so on. Unlinked runners are always 0.
	 */
	trail: number;
	/**
	 * Seconds left before this runner starts moving. Launched blocks pause at the
	 * start of the track for a moment so the player can see them arrive.
	 */
	wait: number;
}

/**
 * Something that happened during play. The rules add these to
 * `state.events`; animations and sound read them.
 *
 * This is a "discriminated union": every variant has a `type` field with a
 * different fixed string, and TypeScript uses that field to know which other
 * fields exist. Inside `if (event.type === "clear")`, for example, TypeScript
 * knows `event.index` exists.
 */
export type GameEvent =
	/** Blocks went onto the track. A linked group launches together, so `count` can be more than 1. */
	| { type: "launch"; count: number; sources: Source[] }
	/** The player clicked a block that can't launch right now (for example, the track is full). */
	| { type: "blocked" }
	/** A runner cleared a cell. `slot` is where on the track it fired from, so the shot animation knows where to start. */
	| { type: "clear"; index: number; color: Color; slot: number }
	/** The tray can now hold every remaining block, so the game started sending them round itself (see `autoLaunch`). */
	| { type: "autoplay" }
	/**
	 * Blocks finished a lap with count left and went into the tray. `blocks`
	 * lists the new tray blocks and where on the track each one was, so the
	 * renderer can fly them from there to the tray.
	 */
	| { type: "stow"; count: number; blocks: StowedBlock[] }
	/**
	 * Blocks used up their counts and left the track. `blocks` says which ones and
	 * where they were (track position in slots), so the renderer can pop them there.
	 */
	| { type: "retire"; count: number; blocks: RetiredBlock[] }
	| { type: "end"; status: "won" | "lost" };

/**
 * Just the "clear" variant of GameEvent. `Extract` is a built-in TypeScript
 * helper that picks matching members out of a union type.
 */
export type ClearEvent = Extract<GameEvent, { type: "clear" }>;

/** A block that just went into the tray: the tray's block object, and where on the track it came from. */
export interface StowedBlock {
	block: Block;
	/** Track position in slots, like `Runner.position`. */
	position: number;
}

/**
 * A used-up block that just left play: its color and where it was. Usually
 * that's on the track (`position` in slots, like `Runner.position`). A linked
 * block that went into the tray already used up waits there for the rest of
 * its group; when they're all used up it leaves from its tray `slot`.
 */
export type RetiredBlock =
	| { color: Color; from: "track"; position: number }
	| { color: Color; from: "tray"; slot: number };

/**
 * Where a block can be launched from: the front of a column, or a tray slot.
 * Only the front block of a column can be launched; the ones behind it have
 * to wait their turn.
 */
export type Source = { from: "column" | "tray"; index: number };

/** Is the level still being played, or has it been won or lost? */
export type Status = "playing" | "won" | "lost";
/**
 * Why a level was lost: "stuck" means nothing left can ever clear another
 * cell; "trayFull" means a block finished its lap with nowhere to go.
 */
export type LossReason = "stuck" | "trayFull";

/** Default number of tray slots, when a level doesn't say. */
export const TRAY_SIZE = 5;
/** Default limit on blocks on the track at the same time. */
export const MAX_RUNNERS = 5;
/** Default speed of runners, in track slots per second. */
export const DEFAULT_SPEED = 10;
/** Default distance between linked blocks on the track, in slots. */
export const TRAIN_GAP = 1;

/**
 * Everything about a level that's being played. Functions in this file
 * change it in place; nothing here is copied unless a caller does so.
 */
export interface GameState {
	grid: Grid;
	/**
	 * The block columns below the grid. Each column is an array whose first
	 * element (index 0) is the front block, the only one that can be launched.
	 */
	columns: Block[][];
	/**
	 * How tall the tallest column was at the start. The layout uses this so the
	 * board doesn't jump around as columns get shorter.
	 */
	columnDepth: number;
	/**
	 * The tray, one entry per slot: a block, or `null` for an empty slot. Blocks
	 * fill from the left, and when one leaves, the rest slide left to close the
	 * gap (see `compactTray`).
	 */
	tray: (Block | null)[];
	/** Blocks currently on the track, in the order they were launched. */
	runners: Runner[];
	/** The most blocks allowed on the track at once. Launching more is refused. */
	maxRunners: number;
	/**
	 * How many seconds a newly launched block waits at the start before moving.
	 * The renderer sets this to match its launch animation (0 means start at once).
	 */
	launchDelay: number;
	/** How fast runners move, in track slots per second. */
	speed: number;
	/**
	 * How many slots apart linked blocks travel. On big grids a slot is much
	 * narrower than a block on the track, so trains need a bigger gap to avoid
	 * overlapping. It changes when each block fires, so it's part of the rules
	 * (and the level generator plans with the same value), not just the drawing.
	 */
	trainGap: number;
	/**
	 * Whether the game is finishing the level by itself: every remaining block
	 * fits in the tray, so nothing can be lost by sending them all round. See
	 * `autoLaunch`.
	 */
	autoplay: boolean;
	/** Starts as "playing"; becomes "won" when the grid is empty, or "lost" when the level can no longer be finished. */
	status: Status;
	/** When `status` is "lost", this says why; otherwise it's `null`. */
	lossReason: LossReason | null;
	/**
	 * A running log of everything that has happened this level, oldest first.
	 * Nothing is ever removed from it ("append-only"), so readers can remember
	 * how far they've read and pick up only the new entries each frame.
	 */
	events: GameEvent[];
}

/**
 * Settings for `createState`. Every field is optional (`?`); anything left
 * out falls back to the defaults defined above.
 */
export interface StateOptions {
	speed?: number;
	traySize?: number;
	maxRunners?: number;
	trainGap?: number;
}

/**
 * Create a level ready to play: the given grid and columns, an empty tray,
 * nothing on the track.
 *
 * The `{ speed = DEFAULT_SPEED, ... }: StateOptions = {}` parameter uses
 * "destructuring with defaults": it pulls named fields out of the options
 * object and supplies a default for any that are missing.
 */
export function createState(
	grid: Grid,
	columns: Block[][],
	{
		speed = DEFAULT_SPEED,
		traySize = TRAY_SIZE,
		maxRunners = MAX_RUNNERS,
		trainGap = TRAIN_GAP,
	}: StateOptions = {},
): GameState {
	return {
		grid,
		columns,
		columnDepth: Math.max(1, ...columns.map((c) => c.length)),
		tray: Array.from({ length: traySize }, () => null),
		runners: [],
		maxRunners,
		launchDelay: 0,
		speed,
		trainGap,
		autoplay: false,
		status: "playing",
		lossReason: null,
		events: [],
	};
}

/** Whether the track has room for at least one more block right now. */
export function canLaunch(state: GameState): boolean {
	return state.status === "playing" && state.runners.length < state.maxRunners;
}

/** The block at a launch source, or `undefined` if that column or tray slot is empty. */
function blockAt(state: GameState, source: Source): Block | undefined {
	return source.from === "column"
		? state.columns[source.index]?.[0]
		: (state.tray[source.index] ?? undefined);
}

/**
 * Work out what clicking `source` would launch, without changing anything.
 *
 * Returns the list of sources that would launch (the clicked block first,
 * then any blocks linked to it), or `null` if the click would do nothing:
 * the source is empty, a linked block isn't reachable yet (not at the front
 * of its column), or the track doesn't have room for all of them.
 *
 * Splitting "what would happen" (this function) from "make it happen"
 * (`launchFrom`) lets the renderer dim blocks that can't launch, using the
 * exact same rules.
 */
export function planLaunch(state: GameState, source: Source): Source[] | null {
	if (state.status !== "playing") return null;
	const block = blockAt(state, source);
	if (!block) return null;

	const sources = [source];
	if (block.link !== undefined) {
		const { link } = block;
		const total =
			state.columns.flat().filter((b) => b.link === link).length +
			state.tray.filter((b) => b?.link === link).length;
		state.columns.forEach((column, index) => {
			if (
				column[0]?.link === link &&
				!(source.from === "column" && source.index === index)
			) {
				sources.push({ from: "column", index });
			}
		});
		state.tray.forEach((b, index) => {
			if (
				b?.link === link &&
				!(source.from === "tray" && source.index === index)
			) {
				sources.push({ from: "tray", index });
			}
		});
		if (sources.length !== total) return null;
		// Linked blocks go into the tray one by one as they finish their lap, so
		// part of a group can be in the tray while the rest is still on the track.
		// It can't be sent round again until all of it is back.
		if (state.runners.some((r) => r.link === link)) return null;
	}

	if (state.runners.length + sources.length > state.maxRunners) return null;
	return sources;
}

/**
 * Launch the block at `source`, along with any blocks linked to it. Returns
 * `true` if something launched. Linked blocks are placed `trainGap` slots
 * apart, so they travel as a train.
 */
export function launchFrom(state: GameState, source: Source): boolean {
	const sources = planLaunch(state, source);
	if (!sources) {
		if (state.status === "playing" && blockAt(state, source))
			state.events.push({ type: "blocked" });
		return false;
	}
	state.events.push({ type: "launch", count: sources.length, sources });
	sources.forEach((src, trail) => {
		const block =
			src.from === "column"
				? state.columns[src.index]?.shift()
				: state.tray[src.index];
		if (src.from === "tray") state.tray[src.index] = null;
		if (block)
			state.runners.push({
				...block,
				position: trail === 0 ? 0 : -trail * state.trainGap,
				trail,
				wait: state.launchDelay,
			});
	});
	compactTray(state);
	return true;
}

/**
 * Slide tray blocks left to fill empty slots, keeping their order. The same
 * block objects are moved (not copied), which lets the renderer recognise
 * each block and animate it sliding.
 */
function compactTray(state: GameState): void {
	const blocks = state.tray.filter((b) => b !== null);
	state.tray = state.tray.map((_, i) => blocks[i] ?? null);
}

/**
 * Whether every block not yet in the tray (on the track or in the columns)
 * would fit in the tray's empty slots. Once that's true, the tray can never
 * overflow, so the game can safely play the rest of the level by itself.
 */
export function canAutoplay(state: GameState): boolean {
	const free = state.tray.filter((b) => b === null).length;
	const waiting = state.columns.reduce((sum, c) => sum + c.length, 0);
	return state.runners.length + waiting <= free;
}

/**
 * The end-of-level helper: once every remaining block fits in the tray (see
 * `canAutoplay`), send blocks round automatically, tray blocks first, then
 * the columns from left to right. The game loop calls this every frame; it
 * launches at most one block (or linked group) per call, and only once the
 * last one has moved clear of the start, so they don't pile up there.
 * Returns `true` if it launched something.
 *
 * This is safe: nothing can ever be lost to a full tray, and with every
 * block going round again and again, each color's blocks eventually reach
 * each cell of their color. It isn't part of `update` so the level
 * generator's replay and the bot, which choose every launch themselves,
 * aren't affected.
 */
export function autoLaunch(state: GameState): boolean {
	if (state.status !== "playing") return false;
	if (!state.autoplay) {
		if (!canAutoplay(state)) return false;
		state.autoplay = true;
		state.events.push({ type: "autoplay" });
	}
	// Wait until the start is clear: every block on the track has moved at
	// least a train gap plus one slot (blocks still waiting to launch, or
	// trailing behind a train leader, have positions at or below zero).
	const clear = state.trainGap + 1;
	if (state.runners.some((r) => r.position < clear)) return false;
	const sources: Source[] = [
		...state.tray.map((_, index) => ({ from: "tray" as const, index })),
		...state.columns.map((_, index) => ({ from: "column" as const, index })),
	];
	const source = sources.find((src) => planLaunch(state, src) !== null);
	return source ? launchFrom(state, source) : false;
}

/** Launch the front block of a column. A convenience wrapper around `launchFrom`. */
export function launch(state: GameState, column: number): boolean {
	return launchFrom(state, { from: "column", index: column });
}

/** Launch the block in a tray slot. A convenience wrapper around `launchFrom`. */
export function launchFromTray(state: GameState, slot: number): boolean {
	return launchFrom(state, { from: "tray", index: slot });
}

/**
 * The index of the first non-empty cell you'd reach starting from `side` of
 * the grid and moving inward along row or column `line`. Returns `null` if
 * that whole line is empty. Runners can only ever hit this cell.
 */
export function nearestCell(
	grid: Grid,
	side: Side,
	line: number,
): number | null {
	const { width, height, cells } = grid;
	const count = side === "top" || side === "bottom" ? height : width;
	for (let i = 0; i < count; i++) {
		let index: number;
		switch (side) {
			case "bottom":
				index = (height - 1 - i) * width + line;
				break;
			case "top":
				index = i * width + line;
				break;
			case "right":
				index = line * width + (width - 1 - i);
				break;
			case "left":
				index = line * width + i;
				break;
		}
		if (cells[index] != null) return index;
	}
	return null;
}

/**
 * A runner's attempt to clear a cell: if the nearest cell along this line is
 * `color`, remove it and return its index; otherwise change nothing and
 * return `null`.
 */
export function fire(
	grid: Grid,
	side: Side,
	line: number,
	color: Color,
): number | null {
	const index = nearestCell(grid, side, line);
	if (index === null || grid.cells[index] !== color) return null;
	grid.cells[index] = null;
	return index;
}

/**
 * Every color that some runner could hit right now: the colors of the
 * nearest cell along every row and column, from every side. A `Set` is used
 * because it stores each color only once.
 */
export function exposedColors(grid: Grid): Set<Color> {
	const colors = new Set<Color>();
	for (const side of SIDES) {
		const lines =
			side === "top" || side === "bottom" ? grid.width : grid.height;
		for (let line = 0; line < lines; line++) {
			const index = nearestCell(grid, side, line);
			const cell = index === null ? null : grid.cells[index];
			if (cell != null) colors.add(cell);
		}
	}
	return colors;
}

/**
 * Whether a runner may leave the track as used up: its count is zero and, if
 * it's linked, so is every other block in its group, whether that block is on
 * the track or already back in the tray.
 */
function usedUp(state: GameState, runner: Runner): boolean {
	if (runner.link === undefined) return runner.count === 0;
	const { link } = runner;
	return (
		state.runners.every((r) => r.link !== link || r.count === 0) &&
		state.tray.every((b) => b?.link !== link || b.count === 0)
	);
}

/**
 * Like `exposedColors`, but counting: for each color, how many row and column
 * ends currently show it. The bot and the level generator use this to judge
 * which colors are easy to reach.
 */
export function exposedCounts(grid: Grid): Map<Color, number> {
	const counts = new Map<Color, number>();
	for (const side of SIDES) {
		const lines =
			side === "top" || side === "bottom" ? grid.width : grid.height;
		for (let line = 0; line < lines; line++) {
			const index = nearestCell(grid, side, line);
			const color = index === null ? null : grid.cells[index];
			if (color != null) counts.set(color, (counts.get(color) ?? 0) + 1);
		}
	}
	return counts;
}

/**
 * Move a runner into the first empty tray slot. Returns the new tray block
 * with where it came from, or `null` if the tray is full.
 */
function stow(state: GameState, runner: Runner): StowedBlock | null {
	const free = state.tray.indexOf(null);
	if (free === -1) return null;
	const { color, count, link, position } = runner;
	const block: Block =
		link === undefined ? { color, count } : { color, count, link };
	state.tray[free] = block;
	return { block, position };
}

/**
 * Advance the game by `dt` ("delta time", in seconds since the last update).
 * The game loop calls this many times a second.
 *
 * What happens in one update:
 * 1. Each runner moves forward. It's checked at every whole slot it passes,
 *    one at a time, so a fast runner can't skip over a cell. All runners'
 *    slots are handled in the order they're reached, across runners.
 * 2. At the end of a lap, a runner (or linked train) goes into the tray, or
 *    the game is lost if there's no room.
 * 3. At other slots, the runner fires inward at the row or column it faces.
 * 4. Used-up runners leave the track.
 * 5. Finally, check whether the level is won or can no longer be won.
 */
export function update(state: GameState, dt: number): void {
	if (state.status !== "playing") return;
	const { grid } = state;
	const length = trackLength(grid.width, grid.height);
	const leaving = new Set<Runner>();

	// 1. Move every runner, noting each whole slot it passes and when (in
	// seconds into this update). A launched runner holds still for its `wait`
	// first. Positions are rounded a tiny amount so that blocks a whole number
	// of slots apart (a linked train) stay exactly that far apart, despite
	// rounding errors in floating-point arithmetic.
	const crossings: {
		runner: Runner;
		k: number;
		time: number;
		order: number;
	}[] = [];
	state.runners.forEach((runner, order) => {
		const start = runner.position;
		const held = Math.min(runner.wait, dt);
		runner.wait -= held;
		runner.position =
			Math.round((start + state.speed * (dt - held)) * 1e9) / 1e9;
		for (let k = Math.floor(start) + 1; k <= runner.position; k++) {
			const time = held + (k - start) / state.speed;
			crossings.push({ runner, k, time: Math.round(time * 1e9), order });
		}
	});
	// 2. Handle the crossings in the order they happen; runners passing slots at
	// the same moment (a linked train) go in launch order, front first. This
	// makes the result the same however the time is split into updates: one
	// big step or many small ones. (Handling each runner's whole move in turn
	// would let a fast runner clear cells "before" a slower one reached them.)
	// That matters because the level generator proves levels winnable by
	// replaying them in steps of exactly one slot, and players' frame rates vary.
	crossings.sort((a, b) => a.time - b.time || a.order - b.order);
	/** Runners that are finished for this update (gone to the tray, or used up at the start corner). */
	const done = new Set<Runner>();
	for (const { runner, k } of crossings) {
		if (leaving.has(runner) || done.has(runner)) continue;
		// Back at the start corner: the runner goes into the tray. Linked blocks
		// go in one by one as each arrives, so tray slots can free up (by
		// relaunching other blocks) while the rest of a train is still on its way.
		// If there's no room, the game is lost.
		//
		// A used-up linked block doesn't take a tray slot while others in its
		// group are still on the track with count left: it rides along without firing, and
		// leaves with them when they're all used up. If instead the rest go into
		// the tray, it joins them there (see the end of this branch).
		const others =
			runner.link === undefined
				? []
				: state.runners.filter(
						(r) => r !== runner && r.link === runner.link && !leaving.has(r),
					);
		const ridesOn = runner.count === 0 && others.some((r) => r.count > 0);
		if (k > 0 && k % length === 0 && !ridesOn) {
			// If it and its whole linked group are already used up (perhaps earlier
			// in this same update), it leaves the track below instead.
			if (usedUp(state, runner)) {
				done.add(runner);
				continue;
			}
			// Any used-up blocks of its group still on the track would never go
			// anywhere else, so they go into the tray too.
			const going = [
				runner,
				...(others.every((r) => r.count === 0) ? others : []),
			];
			for (const r of going) {
				const stowed = stow(state, r);
				if (!stowed) {
					lose(state, "trayFull");
					return;
				}
				leaving.add(r);
				state.events.push({ type: "stow", count: 1, blocks: [stowed] });
			}
			continue;
		}
		if (runner.count === 0) continue; // used up, but waiting for its linked group: rides along without firing
		const slotIndex = ((k % length) + length) % length;
		const slot = slotAt(slotIndex, grid.width, grid.height);
		if (slot.kind !== "face") continue;
		const cleared = fire(grid, slot.side, slot.line, runner.color);
		if (cleared !== null) {
			runner.count -= 1;
			state.events.push({
				type: "clear",
				index: cleared,
				color: runner.color,
				slot: slotIndex,
			});
		}
	}
	for (const runner of state.runners)
		if (runner.position >= length) runner.position -= length;

	// Remove used-up runners. A linked runner only leaves once every block in
	// its group is used up; any of the group already in the tray (used up, and
	// waiting for the others) leaves the tray at the same time.
	const retired: RetiredBlock[] = [];
	const doneLinks = new Set<number>();
	for (const runner of state.runners) {
		if (leaving.has(runner)) continue;
		if (usedUp(state, runner)) {
			leaving.add(runner);
			retired.push({
				color: runner.color,
				from: "track",
				position: runner.position,
			});
			if (runner.link !== undefined) doneLinks.add(runner.link);
		}
	}
	if (doneLinks.size > 0) {
		state.tray.forEach((block, slot) => {
			if (block?.link === undefined || !doneLinks.has(block.link)) return;
			retired.push({ color: block.color, from: "tray", slot });
			state.tray[slot] = null;
		});
		compactTray(state);
	}
	if (retired.length > 0)
		state.events.push({
			type: "retire",
			count: retired.length,
			blocks: retired,
		});
	state.runners = state.runners.filter((r) => !leaving.has(r));

	if (grid.cells.every((c) => c === null)) {
		state.status = "won";
		state.events.push({ type: "end", status: "won" });
	} else if (isStuck(state)) {
		lose(state, "stuck");
	}
}

/** End the level as lost, recording why and logging an event. */
function lose(state: GameState, reason: LossReason): void {
	state.status = "lost";
	state.lossReason = reason;
	state.events.push({ type: "end", status: "lost" });
}

/**
 * Whether the level can never be finished: no block anywhere (in a column,
 * the tray, or on the track) has a color that matches any reachable cell.
 * Once that's true, nothing can ever change, so the game is lost.
 */
function isStuck(state: GameState): boolean {
	// The colors that some block could still clear.
	const usable = new Set<Color>();
	const add = (b: Block | null) => {
		if (b && b.count > 0) usable.add(b.color);
	};
	for (const runner of state.runners) add(runner);
	for (const block of state.tray) add(block);
	for (const column of state.columns) for (const block of column) add(block);

	// Look at the reachable cell of each row and column, and stop at the first
	// one some block could clear. This runs after every update, so stopping
	// early matters: on large grids, scanning every line each time made level
	// generation take minutes.
	const { grid } = state;
	for (const side of SIDES) {
		const lines =
			side === "top" || side === "bottom" ? grid.width : grid.height;
		for (let line = 0; line < lines; line++) {
			const index = nearestCell(grid, side, line);
			const color = index === null ? null : grid.cells[index];
			if (color != null && usable.has(color)) return false;
		}
	}
	return true;
}
